import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleMap } from "@react-google-maps/api";
import { useGoogleMaps } from "../../../config/google-maps-loader";
import { useSocket } from "../../../context/socket-context";
import apiUrls from "../../../config/api-urls";
import { confirmPaymentReceived } from "../../../services/scheduled-tasks-api";
import tokenService from "../../../services/token-service";
import toast from "react-hot-toast";
import CompleteJobDialog from "../modals/complete-job-dialog";
import JobApprovalDialog from "../modals/job-approval-dialog";
import DisputeDialog from "../modals/dispute-dialog";
import Chat from "../../customer/schedule/chat";
import {
  Phone,
  MessageCircle,
  MapPin,
  Loader2,
  Navigation,
  CheckCircle,
  XCircle,
  Play,
  Flag,
  Wrench,
  Clock,
  User,
  ArrowLeft,
  AlertTriangle,
} from "lucide-react";

const ActiveJobTracking = ({ job, onBack, onJobComplete }) => {
  const navigate = useNavigate();
  const { isLoaded } = useGoogleMaps();
  const mapRef = useRef(null);
  const handymanMarkerRef = useRef(null);
  const customerMarkerRef = useRef(null);
  const watchIdRef = useRef(null);
  const lastCancellationHandledRef = useRef(null);

  const { socket, joinTaskRoom, leaveTaskRoom, isConnected } = useSocket();

  const [status, setStatus] = useState(job?.status || "assigned");
  const [liveJob, setLiveJob] = useState(job || null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [eta, setEta] = useState(null);
  const [distance, setDistance] = useState(null);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState("waiting");
  const [showRateCustomerDialog, setShowRateCustomerDialog] = useState(false);
  const [customerRating, setCustomerRating] = useState(5);
  const [customerReview, setCustomerReview] = useState("");
  const [isSubmittingCustomerRating, setIsSubmittingCustomerRating] = useState(false);
  const [customerRatingError, setCustomerRatingError] = useState("");
  const [isMarkingPaymentReceived, setIsMarkingPaymentReceived] = useState(false);
  const [hasRatedCustomer, setHasRatedCustomer] = useState(false);
  const [customerMarkedCompleted, setCustomerMarkedCompleted] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showDisputeDialog, setShowDisputeDialog] = useState(false);

  const token = tokenService.getToken();
  const taskId =
    liveJob?.serviceRequestId ||
    liveJob?._id ||
    job?.serviceRequestId ||
    job?._id;

  useEffect(() => {
    if (job) {
      setLiveJob(job);
    }
  }, [job]);

  useEffect(() => {
    if (job?.status) {
      setStatus(job.status);
    }
  }, [job?.status]);

  // Extract customer and service details from the accepted offer data
  const customerName = liveJob?.customerName || job?.customerName || "Customer";
  const customerPhone =
    liveJob?.customerPhoneNumber ||
    liveJob?.customerPhone ||
    job?.customerPhoneNumber ||
    job?.customerPhone;
  const customerId =
    liveJob?.customerId ||
    liveJob?.customer?._id ||
    liveJob?.customer ||
    job?.customerId ||
    job?.customer?._id ||
    job?.customer;
  const customerAvgRating = Number(liveJob?.customerRating ?? job?.customerRating ?? 0);
  const customerAvgRatingCount = Number(
    liveJob?.customerRatingCount ?? job?.customerRatingCount ?? 0
  );
  const offerPrice =
    liveJob?.finalPrice ??
    liveJob?.offerDetails?.proposedPrice ??
    job?.finalPrice ??
    job?.offerDetails?.proposedPrice;
  const serviceTitle = liveJob?.mainService || job?.mainService;
  const serviceAddress =
    liveJob?.serviceDetails?.location?.address ||
    job?.serviceDetails?.location?.address ||
    liveJob?.address ||
    job?.address;
  const subServices =
    liveJob?.serviceDetails?.subServices ||
    job?.serviceDetails?.subServices ||
    liveJob?.subServices ||
    job?.subServices ||
    [];

  useEffect(() => {
    const hydrateActiveJob = async () => {
      if (!token || !taskId) return;

      const missingCustomer = !customerId;
      const missingCoreDetails = !serviceTitle || !serviceAddress;
      if (!missingCustomer && !missingCoreDetails) return;

      try {
        const res = await fetch(apiUrls.handymanJob.getActiveJob, {
          method: "GET",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) return;

        const incoming = data?.data;
        const incomingId = incoming?.serviceRequestId || incoming?._id;
        if (!incoming || !incomingId || incomingId !== taskId) return;

        setLiveJob(incoming);
        if (incoming?.status) setStatus(incoming.status);
      } catch {
        // ignore hydration failures
      }
    };

    hydrateActiveJob();
  }, [token, taskId, customerId, serviceTitle, serviceAddress]);

  // Customer location
  const customerPos = {
    lat: job?.serviceDetails?.location?.coordinates?.[1] || job?.coordinates?.lat || job?.location?.coordinates?.[1] || 31.5204,
    lng: job?.serviceDetails?.location?.coordinates?.[0] || job?.coordinates?.lng || job?.location?.coordinates?.[0] || 74.3587,
  };

  // Status flow
  const statusFlow = ["assigned", "on_the_way", "arrived", "work_started", "in_progress", "approved", "completed"];

  const statusLabel = {
    assigned: "Assigned",
    on_the_way: "On The Way",
    arrived: "Arrived",
    work_started: "Working",
    in_progress: "Awaiting Confirmation",
    approved: "Approved",
    payment_received: "Payment Received",
    completed: "Completed",
    cancelled: "Cancelled",
  };

  const statusColor = {
    assigned: "bg-blue-100 text-blue-700 border-blue-200",
    on_the_way: "bg-yellow-100 text-yellow-700 border-yellow-200",
    arrived: "bg-purple-100 text-purple-700 border-purple-200",
    work_started: "bg-indigo-100 text-indigo-700 border-indigo-200",
    in_progress: "bg-green-100 text-green-700 border-green-200",
    approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
    payment_received: "bg-emerald-100 text-emerald-700 border-emerald-200",
    completed: "bg-gray-100 text-gray-700 border-gray-200",
    cancelled: "bg-red-100 text-red-700 border-red-200",
  };

  // Join task room
  useEffect(() => {
    if (taskId && isConnected) {
      joinTaskRoom(taskId);
    }
    return () => {
      if (taskId && leaveTaskRoom) {
        leaveTaskRoom(taskId);
      }
    };
  }, [taskId, isConnected]);

  // Listen for dispute created
  useEffect(() => {
    if (!socket) return;

    const handleDisputeCreated = (data) => {
      if (data.disputeId) {
        setStatus("disputed");
        toast.error(`A dispute has been raised by the ${data.raisedBy}. Reason: ${data.reason}`);
        setTimeout(() => {
          if (onBack) {
            onBack();
          } else {
            navigate('/handyman/my-jobs');
          }
        }, 2000);
      }
    };

    socket.on("dispute:created", handleDisputeCreated);

    return () => {
      socket.off("dispute:created", handleDisputeCreated);
    };
  }, [socket, navigate, onBack]);

  // Listen for task completion by customer
  useEffect(() => {
    if (!socket) return;

    const handleTaskCompleted = (data) => {
      if (data.taskId === taskId) {
        setStatus("completed");
        setApprovalStatus("approved");
        setShowApprovalDialog(true);
        setCustomerMarkedCompleted(true);
        toast.success("Customer confirmed task completion! 🎉");
      }
    };

    socket.on("taskCompleted", handleTaskCompleted);

    return () => {
      socket.off("taskCompleted", handleTaskCompleted);
    };
  }, [socket, taskId, onJobComplete]);

  useEffect(() => {
    if (customerMarkedCompleted && hasRatedCustomer && onJobComplete) {
      onJobComplete();
    }
  }, [customerMarkedCompleted, hasRatedCustomer, onJobComplete]);

  // Listen for customer approval/rejection updates
  useEffect(() => {
    if (!socket) return;

    const handleJobStatusUpdate = (data) => {
      const incomingTaskId = data?.taskId || data?.serviceRequestId;
      if (incomingTaskId && taskId && incomingTaskId !== taskId) return;

      const nextStatus = data?.status;
      if (nextStatus) {
        setStatus(nextStatus);
      }

      if (data && typeof data === "object") {
        setLiveJob((prev) => ({
          ...(prev || {}),
          ...data,
        }));
      }

      const nextFinalPrice = data?.finalPrice ?? data?.offerPrice ?? data?.price;
      const paymentReceived = Boolean(data?.paymentReceived);
      const message = data?.message;

      if (nextStatus === "disputed") {
        toast.error("A dispute has been raised for this job.");
        setTimeout(() => {
          navigate('/handyman/my-jobs');
        }, 2000);
        return;
      }

      if (nextStatus === "cancelled") {
        const reason = (data?.reason || "").trim();
        const cancelledBy = data?.cancelledBy;
        const eventKey = `${incomingTaskId || ""}:${cancelledBy || ""}:${reason || ""}:${
          data?.timestamp || ""
        }`;

        if (lastCancellationHandledRef.current !== eventKey) {
          lastCancellationHandledRef.current = eventKey;
          const message =
            data?.message ||
            (cancelledBy === "customer"
              ? `Customer cancelled the request${reason ? `: ${reason}` : "."}`
              : cancelledBy === "handyman"
                ? `You cancelled the request${reason ? `: ${reason}` : "."}`
                : `Job cancelled${reason ? `: ${reason}` : "."}`);
          toast(message);
        }

        setTimeout(() => {
          if (onBack) {
            onBack();
          } else {
            navigate('/handyman/my-jobs');
          }
        }, 1500);
        return;
      }

      if (nextStatus === "approved") {
        setApprovalStatus("approved");
        setShowApprovalDialog(true);
        if (paymentReceived) {
          toast.success(
            `Customer approved your work. Payment received: PKR ${(nextFinalPrice ?? offerPrice)?.toLocaleString?.() ?? (nextFinalPrice ?? offerPrice)}`
          );
        } else {
          toast.success(
            `Customer approved your work. Final price: PKR ${(nextFinalPrice ?? offerPrice)?.toLocaleString?.() ?? (nextFinalPrice ?? offerPrice)}`
          );
        }
        return;
      }

      if (paymentReceived) {
        toast.success(
          message ||
          `Payment received: PKR ${(nextFinalPrice ?? offerPrice)?.toLocaleString?.() ?? (nextFinalPrice ?? offerPrice)}`
        );
        return;
      }

      if (nextStatus === "notApproved" || nextStatus === "rejected") {
        setApprovalStatus("notApproved");
        setShowApprovalDialog(true);
        toast.error("Customer did not approve the work.");
      }
    };

    socket.on("job:status:update", handleJobStatusUpdate);

    return () => {
      socket.off("job:status:update", handleJobStatusUpdate);
    };
  }, [socket, taskId, navigate]);

  // Get current location
  const getCurrentLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported"));
        return;
      }

      const onSuccess = (position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setCurrentLocation(pos);
        resolve(pos);
      };

      const onError = (error) => {
        if (error?.code === error.TIMEOUT) {
          navigator.geolocation.getCurrentPosition(
            onSuccess,
            (retryError) => {
              let errorMessage = "Unable to get location";
              switch (retryError.code) {
                case retryError.PERMISSION_DENIED:
                  errorMessage = "Location permission denied. Please enable location access.";
                  break;
                case retryError.POSITION_UNAVAILABLE:
                  errorMessage = "Location information unavailable.";
                  break;
                case retryError.TIMEOUT:
                  errorMessage = "Location request timed out. Please try again.";
                  break;
              }
              reject(new Error(errorMessage));
            },
            { enableHighAccuracy: false, timeout: 30000, maximumAge: 10000 }
          );
          return;
        }

        let errorMessage = "Unable to get location";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location permission denied. Please enable location access.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out. Please try again.";
            break;
        }
        reject(new Error(errorMessage));
      };

      navigator.geolocation.getCurrentPosition(onSuccess, onError, {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0,
      });
    });
  }, []);

  // Start watching location when on_the_way
  useEffect(() => {
    if (status === "on_the_way" && navigator.geolocation) {
      watchIdRef.current = navigator.geolocation.watchPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setCurrentLocation(pos);

          // Update marker
          if (handymanMarkerRef.current) {
            handymanMarkerRef.current.setPosition(pos);
          }

          // Pan map
          if (mapRef.current) {
            mapRef.current.panTo(pos);
          }

          // Send location update to backend
          updateLocationToServer(pos);
        },
        (error) => console.error("Watch position error:", error),
        { enableHighAccuracy: true, maximumAge: 5000 }
      );
    }

    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [status]);

  // Update location to server
  const updateLocationToServer = async (pos) => {
    try {
      await fetch(apiUrls.handymanJob.updateLocation, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          taskId,
          location: {
            type: "Point",
            coordinates: [pos.lng, pos.lat],
          },
        }),
      });
    } catch (error) {
      console.error("Failed to update location:", error);
    }
  };

  // Calculate distance using Haversine
  const calculateDistance = (pos1, pos2) => {
    const R = 6371;
    const dLat = ((pos2.lat - pos1.lat) * Math.PI) / 180;
    const dLng = ((pos2.lng - pos1.lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((pos1.lat * Math.PI) / 180) *
      Math.cos((pos2.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Calculate ETA
  useEffect(() => {
    if (currentLocation && customerPos) {
      const distKm = calculateDistance(currentLocation, customerPos);
      setDistance(`${distKm.toFixed(1)} km`);
      const etaMinutes = Math.round((distKm / 30) * 60);
      setEta(etaMinutes < 60 ? `${etaMinutes} mins` : `${Math.floor(etaMinutes / 60)} hr ${etaMinutes % 60} mins`);
    }
  }, [currentLocation, customerPos]);

  // Initialize map markers
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;

    // Customer marker (blue)
    if (!customerMarkerRef.current) {
      customerMarkerRef.current = new window.google.maps.Marker({
        position: customerPos,
        map: mapRef.current,
        title: "Customer Location",
        icon: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
      });
    }

    // Get initial location and create handyman marker
    getCurrentLocation().then((pos) => {
      if (!handymanMarkerRef.current) {
        handymanMarkerRef.current = new window.google.maps.Marker({
          position: pos,
          map: mapRef.current,
          title: "Your Location",
          icon: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
        });
      }

      // Fit bounds to show both markers
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(pos);
      bounds.extend(customerPos);
      mapRef.current.fitBounds(bounds, { padding: 50 });
    });

    return () => {
      if (customerMarkerRef.current) {
        customerMarkerRef.current.setMap(null);
        customerMarkerRef.current = null;
      }
      if (handymanMarkerRef.current) {
        handymanMarkerRef.current.setMap(null);
        handymanMarkerRef.current = null;
      }
    };
  }, [isLoaded]);

  // API calls for job lifecycle
  const startJourney = async () => {
    setIsLoading(true);
    try {
      // Get current location with proper error handling
      const pos = await getCurrentLocation();

      // Make API call with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const res = await fetch(apiUrls.handymanJob.startJourney, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          taskId,
          location: { type: "Point", coordinates: [pos.lng, pos.lat] },
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to start journey");
      }

      setStatus("on_the_way");
      toast.success("Journey started! Customer can now track you.");
    } catch (error) {
      if (error.name === 'AbortError') {
        toast.error("Request timed out. Please check your internet connection and try again.");
      } else {
        toast.error(error.message || "Failed to start journey");
      }
      console.error("Start journey error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const markArrived = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(apiUrls.handymanJob.arrived, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ taskId }),
      });

      if (!res.ok) throw new Error("Failed to mark arrived");

      setStatus("arrived");
      toast.success("Marked as arrived!");
    } catch (error) {
      toast.error(error.message || "Failed to mark arrived");
    } finally {
      setIsLoading(false);
    }
  };

  const startWork = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(apiUrls.handymanJob.startWork, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ taskId }),
      });

      if (!res.ok) throw new Error("Failed to start work");

      setStatus("work_started");
      toast.success("Work started!");
    } catch (error) {
      toast.error(error.message || "Failed to start work");
    } finally {
      setIsLoading(false);
    }
  };

  const completeWork = async () => {
    setShowCompleteDialog(true);
  };

  const cancelJob = async () => {
    if (!taskId) {
      toast.error("Task id missing. Please reopen the job.");
      return;
    }

    const reason = (cancelReason || "").trim();
    if (!reason) {
      toast.error("Please enter a cancel reason.");
      return;
    }

    setIsCancelling(true);
    try {
      const res = await fetch(apiUrls.handymanJob.cancelJob, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ taskId, reason }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error?.message || data?.message || "Failed to cancel job");
      }

      setStatus("cancelled");
      setShowCancelDialog(false);
      setCancelReason("");
      toast.success("Job cancelled successfully.");

      // Remove task card after successful cancellation
      setTimeout(() => {
        if (onBack) {
          onBack();
        } else {
          navigate('/handyman/my-jobs');
        }
      }, 1000);
    } catch (error) {
      toast.error(error?.message || "Unable to cancel job");
    } finally {
      setIsCancelling(false);
    }
  };

  const submitWorkCompletion = async ({ pictures, videos, description }) => {
    if (!taskId) {
      toast.error("Task id missing. Please reopen the job.");
      return;
    }

    setIsLoading(true);
    try {
      const fileFieldVariants = [
        { picturesKey: "workPictures", videosKey: "workVideo" },
      ];

      let lastError;
      for (const variant of fileFieldVariants) {
        const formData = new FormData();
        formData.append("taskId", taskId);
        if (description) formData.append("description", description);

        (pictures || []).forEach((file) => {
          formData.append(variant.picturesKey, file);
        });
        (videos || []).forEach((file) => {
          formData.append(variant.videosKey, file);
        });

        const res = await fetch(apiUrls.handymanJob.completeWork, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });

        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          lastError = null;
          break;
        }

        const code = data?.error?.code || data?.code;
        const message = data?.error?.message || data?.message;

        // If multer rejects the file field name, retry with another common variant.
        if (code === "LIMIT_UNEXPECTED_FILE") {
          lastError = new Error(
            `${message || "Unexpected file field"} (tried: ${variant.picturesKey}, ${variant.videosKey})`
          );
          continue;
        }

        throw new Error(message || "Failed to submit completed work");
      }

      if (lastError) {
        throw lastError;
      }

      setShowCompleteDialog(false);
      setStatus("in_progress");
      setApprovalStatus("waiting");
      setShowApprovalDialog(true);
      toast.success("Work submitted! Waiting for customer confirmation.");
    } catch (error) {
      toast.error(error.message || "Failed to submit completed work");
    } finally {
      setIsLoading(false);
    }
  };

  // Get next action based on status
  const getNextAction = () => {
    switch (status) {
      case "assigned":
        return { label: "Start Journey", icon: Navigation, action: startJourney, color: "bg-blue-600 hover:bg-blue-700" };
      case "on_the_way":
        return { label: "I've Arrived", icon: Flag, action: markArrived, color: "bg-purple-600 hover:bg-purple-700" };
      case "arrived":
        return { label: "Start Work", icon: Wrench, action: startWork, color: "bg-indigo-600 hover:bg-indigo-700" };
      case "work_started":
        return { label: "Complete Work", icon: CheckCircle, action: completeWork, color: "bg-green-600 hover:bg-green-700" };
      case "approved":
        return {
          label: "Confirm Payment",
          icon: CheckCircle,
          action: handleConfirmPayment,
          color: "bg-emerald-600 hover:bg-emerald-700"
        };
      default:
        return null;
    }
  };

  const handleConfirmPayment = async () => {
    setIsLoading(true);
    try {
      const result = await confirmPaymentReceived(taskId);
      if (result.success) {
        toast.success("Payment confirmed! Waiting for customer's final completion.");
        setStatus("payment_received");
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      toast.error(error.message || "Failed to confirm payment");
    } finally {
      setIsLoading(false);
    }
  };

  const submitCustomerRating = async () => {
    if (!taskId) {
      setCustomerRatingError("Task id missing. Please reopen the job.");
      return;
    }

    setIsSubmittingCustomerRating(true);
    setCustomerRatingError("");

    try {
      const res = await fetch(apiUrls.handymanJob.rateCustomer, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          taskId,
          rating: customerRating,
          review: customerReview,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data?.error?.message || data?.message || "Failed to submit rating"
        );
      }

      setShowRateCustomerDialog(false);
      setHasRatedCustomer(true);
      toast.success("Rating submitted.");

      // Exit the active tracking screen after rating
      setTimeout(() => {
        if (onBack) {
          onBack();
        }
      }, 1500);
    } catch (error) {
      const message = error?.message || "Unable to submit rating";
      setCustomerRatingError(message);
      toast.error(message);
    } finally {
      setIsSubmittingCustomerRating(false);
    }
  };

  const nextAction = getNextAction();

  const paymentReceived =
    Boolean(liveJob?.paymentReceived?.confirmedByHandyman) || status === "payment_received";

  const markPaymentReceived = async () => {
    if (!taskId) {
      toast.error("Task id missing. Please reopen the job.");
      return;
    }

    setIsMarkingPaymentReceived(true);
    try {
      const res = await fetch(apiUrls.handymanJob.confirmPaymentReceived, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ taskId, confirmed: true }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error?.message || data?.message || "Failed to mark payment received");
      }

      const nextFinalPrice =
        data?.data?.finalPrice ??
        data?.finalPrice ??
        liveJob?.finalPrice ??
        offerPrice;

      setStatus("payment_received");
      setLiveJob((prev) => ({
        ...(prev || {}),
        ...(data?.data || data),
        paymentReceived: {
          ...(prev?.paymentReceived || {}),
          confirmedByHandyman: true,
          confirmedAt: new Date().toISOString(),
        },
        finalPrice: nextFinalPrice,
      }));

      toast.success(
        `Payment received confirmed. PKR ${Number(nextFinalPrice ?? offerPrice).toLocaleString()}`
      );
    } catch (error) {
      toast.error(error?.message || "Unable to update payment status");
    } finally {
      setIsMarkingPaymentReceived(false);
    }
  };

  if (!isLoaded) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gray-100">
        <Loader2 className="w-8 h-8 animate-spin text-[#EFA938]" />
      </div>
    );
  }

  return (
    <div className="relative w-full h-screen">
      {showCancelDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-sm mx-4">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-gray-900 dark:text-white">
                  Cancel Job
                </h2>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Please share a short reason. A 10% penalty may apply if work was already assigned or in progress.
                </p>
              </div>
              <button
                onClick={() => {
                  if (!isCancelling) setShowCancelDialog(false);
                }}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300 rotate-180" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <label className="text-sm font-medium text-gray-800 dark:text-gray-200 block">
                Reason for cancellation
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-gray-200 dark:border-slate-700 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#EFA938] dark:bg-slate-900 dark:text-white"
                placeholder="Why are you cancelling this job?"
              />

              <div className="flex gap-2">
                <button
                  onClick={() => setShowCancelDialog(false)}
                  disabled={isCancelling}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 text-sm text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Close
                </button>
                <button
                  onClick={cancelJob}
                  disabled={isCancelling}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isCancelling ? "Cancelling..." : "Confirm Cancel"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCompleteDialog && (
        <CompleteJobDialog
          onClose={() => setShowCompleteDialog(false)}
          onSubmit={submitWorkCompletion}
        />
      )}

      {showApprovalDialog && (
        <JobApprovalDialog
          onClose={() => setShowApprovalDialog(false)}
          status={approvalStatus}
          finalPrice={liveJob?.finalPrice ?? offerPrice}
          paymentReceived={paymentReceived}
        />
      )}

      {showRateCustomerDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-sm mx-4">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900 dark:text-white">
                Rate Customer
              </h2>
              <button
                onClick={() => setShowRateCustomerDialog(false)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300 rotate-180" />
              </button>
            </div>

            <div className="p-4 space-y-3 max-h-[70vh] overflow-auto">
              <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">Final Price</p>
                <p className="text-lg font-bold text-[#EFA938]">PKR {offerPrice?.toLocaleString()}</p>
                <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
                  Customer approved your work. Payment will be processed.
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Rating</p>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      onClick={() => setCustomerRating(value)}
                      className={`p-2 rounded-lg border transition-colors ${customerRating >= value
                          ? "bg-amber-50 border-amber-300 text-amber-600"
                          : "bg-gray-50 border-gray-200 text-gray-500"
                        }`}
                      aria-label={`Rate ${value} star${value > 1 ? "s" : ""}`}
                    >
                      <span className={customerRating >= value ? "text-amber-500" : "text-gray-400"}>★</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2 block">
                  Review (optional)
                </label>
                <textarea
                  value={customerReview}
                  onChange={(e) => setCustomerReview(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-gray-200 dark:border-slate-700 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#EFA938] dark:bg-slate-900 dark:text-white"
                  placeholder="Share your experience..."
                />
              </div>

              {customerRatingError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {customerRatingError}
                </div>
              )}

              <button
                onClick={submitCustomerRating}
                disabled={isSubmittingCustomerRating}
                className="w-full bg-[#EFA938] text-white py-3 rounded-xl flex justify-center items-center gap-2 hover:bg-[#d78c1f] transition-colors disabled:opacity-60 disabled:cursor-not-allowed font-semibold"
              >
                {isSubmittingCustomerRating && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
                Submit Rating
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Back Button */}
      <button
        onClick={onBack}
        className="absolute top-4 left-4 z-50 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-colors"
      >
        <ArrowLeft className="w-5 h-5 text-gray-700" />
      </button>

      {/* Map */}
      <GoogleMap
        center={customerPos}
        zoom={14}
        onLoad={(map) => (mapRef.current = map)}
        mapContainerStyle={{ width: "100%", height: "100%" }}
        options={{ mapId: "HANDYMAN_TRACKING_MAP" }}
      />

      {/* Status Card - Top Right */}
      <div className="absolute top-4 right-4 bg-white shadow-lg rounded-xl p-4 min-w-[220px] z-40">
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold mb-3 border ${statusColor[status]}`}>
          <div className="w-2 h-2 rounded-full bg-current animate-pulse" />
          {statusLabel[status]}
        </div>

        {status === "on_the_way" && (
          <>
            <p className="text-gray-600 text-sm flex items-center gap-2">
              <Clock className="w-4 h-4" />
              ETA: <strong>{eta || "Calculating..."}</strong>
            </p>
            <p className="text-gray-600 text-sm flex items-center gap-2 mt-1">
              <MapPin className="w-4 h-4" />
              Distance: <strong>{distance || "Calculating..."}</strong>
            </p>
          </>
        )}

        {isConnected && (
          <div className="mt-3 flex items-center gap-1 text-xs text-green-600">
            <div className="w-2 h-2 bg-green-500 rounded-full" />
            Live Tracking Active
          </div>
        )}
      </div>

      {/* Bottom Card */}
      <div className="absolute bottom-0 left-0 right-0 p-4 z-40">
        <div className="bg-white rounded-2xl shadow-2xl p-5">
          {/* Customer Info */}
          <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-100">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xl font-bold">
              {customerName?.charAt(0)?.toUpperCase() || <User className="w-6 h-6" />}
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-gray-900">{customerName}</h2>
              <p className="text-gray-500 text-sm">{serviceTitle}</p>
              <p className="text-xs text-gray-500 mt-1">
                <span className="text-amber-500">★</span> {customerAvgRating.toFixed(1)} ({customerAvgRatingCount})
              </p>
              {subServices.length > 0 && (
                <p className="text-xs text-gray-400 mt-1">
                  {subServices.join(", ")}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500 uppercase">Price</p>
              <p className="text-xl font-bold text-[#EFA938]">PKR {offerPrice}</p>
            </div>
          </div>

          {/* Location */}
          <div className="flex items-start gap-3 mb-4 p-3 bg-gray-50 rounded-xl">
            <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-gray-500 uppercase font-semibold">Customer Location</p>
              <p className="text-sm text-gray-800">{serviceAddress || "Location not provided"}</p>
            </div>
          </div>

          {/* Contact Buttons */}
          <div className="flex gap-3 mb-4">
            <button
              onClick={() => {
                if (customerPhone) {
                  window.location.href = `tel:${customerPhone}`;
                  toast.success(`Calling ${customerName.split(' ')[0]}...`);
                } else {
                  toast.error("Customer phone number not available");
                }
              }}
              disabled={!customerPhone}
              className="flex-1 bg-green-600 text-white py-3 rounded-xl flex justify-center items-center gap-2 hover:bg-green-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg active:scale-95 font-semibold"
            >
              <Phone className="w-5 h-5" /> Call {customerName.split(' ')[0]}
            </button>
            <button
              onClick={() => {
                if (!customerId) {
                  toast.error("Customer id missing. Please reopen this job.");
                  return;
                }
                setShowChat(true);
              }}
              disabled={!customerId}
              className="flex-1 border-2 border-gray-300 text-gray-700 py-3 rounded-xl flex justify-center items-center gap-2 hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MessageCircle className="w-5 h-5" /> Chat
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-4 px-2">
            {statusFlow.slice(0, 5).map((step, idx) => {
              const currentIdx = statusFlow.indexOf(status);
              const isCompleted = idx < currentIdx;
              const isCurrent = idx === currentIdx;

              return (
                <React.Fragment key={step}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${isCompleted
                          ? "bg-green-500 text-white"
                          : isCurrent
                            ? "bg-[#EFA938] text-white ring-4 ring-orange-100"
                            : "bg-gray-200 text-gray-500"
                        }`}
                    >
                      {isCompleted ? "✓" : idx + 1}
                    </div>
                  </div>
                  {idx < 4 && (
                    <div className={`flex-1 h-1 mx-1 rounded ${idx < currentIdx ? "bg-green-500" : "bg-gray-200"}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Action Button */}
          {nextAction && (
            <button
              onClick={nextAction.action}
              disabled={isLoading}
              className={`w-full ${nextAction.color} text-white py-4 rounded-xl flex justify-center items-center gap-3 transition-all font-bold text-lg disabled:opacity-50`}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <nextAction.icon className="w-5 h-5" />
              )}
              {isLoading ? "Processing..." : nextAction.label}
            </button>
          )}

          {!["completed", "cancelled", "disputed"].includes(status) && (
            <div className="flex gap-3 mt-3">
              {["in_progress", "approved"].includes(status) && (
                <button
                  onClick={() => setShowDisputeDialog(true)}
                  disabled={isLoading}
                  className="w-full border-2 border-orange-300 text-orange-600 py-3 rounded-xl flex justify-center items-center gap-2 hover:bg-orange-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed font-semibold"
                >
                  <AlertTriangle className="w-5 h-5" /> Raise Dispute
                </button>
              )}
              {!["work_started", "in_progress", "approved", "payment_received"].includes(status) && (
                <button
                  onClick={() => setShowCancelDialog(true)}
                  disabled={isLoading}
                  className="w-full border-2 border-red-300 text-red-600 py-3 rounded-xl flex justify-center items-center gap-2 hover:bg-red-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed font-semibold"
                >
                  <XCircle className="w-5 h-5" /> Cancel Job
                </button>
              )}
            </div>
          )}

          {(["approved", "payment_received"].includes(status)) && !hasRatedCustomer && (
            <>
              <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Final Price</p>
                    <p className="text-base font-bold text-[#EFA938]">
                      PKR {(liveJob?.finalPrice ?? offerPrice)?.toLocaleString?.() ?? (liveJob?.finalPrice ?? offerPrice)}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {paymentReceived ? "Payment received." : "Collect payment from customer, then mark received."}
                    </p>
                  </div>

                  <button
                    onClick={markPaymentReceived}
                    disabled={paymentReceived || isMarkingPaymentReceived}
                    className="shrink-0 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isMarkingPaymentReceived ? "Saving..." : paymentReceived ? "Received" : "Mark Received"}
                  </button>
                </div>
              </div>

              <button
                onClick={() => setShowRateCustomerDialog(true)}
                disabled={!paymentReceived}
                className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-xl flex justify-center items-center gap-3 transition-all font-bold text-lg disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <CheckCircle className="w-5 h-5" /> Rate Customer
              </button>
            </>
          )}

          {status === "completed" && !hasRatedCustomer && (
            <div className="mt-3">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <p className="text-xs text-gray-500 uppercase">Next step</p>
                <p className="text-sm text-gray-700 mt-1">
                  Please rate the customer to finish.
                </p>
              </div>

              <button
                onClick={() => setShowRateCustomerDialog(true)}
                className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-xl flex justify-center items-center gap-3 transition-all font-bold text-lg"
              >
                <CheckCircle className="w-5 h-5" /> Rate Customer
              </button>
            </div>
          )}

          {(["approved", "payment_received"].includes(status)) && hasRatedCustomer && (
            <div className="text-center py-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-semibold">
                <CheckCircle className="w-4 h-4" /> Customer rated successfully
              </div>
            </div>
          )}

          {status === "in_progress" && (
            <div className="text-center py-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm font-semibold">
                <Loader2 className="w-4 h-4 animate-spin" />
                Waiting for customer to confirm completion...
              </div>
            </div>
          )}

          {status === "completed" && (
            <div className="text-center py-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-semibold">
                <CheckCircle className="w-4 h-4" />
                Job Completed Successfully!
              </div>
            </div>
          )}

          {status === "cancelled" && (
            <div className="text-center py-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-full text-sm font-semibold">
                <XCircle className="w-4 h-4" />
                Job Cancelled
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chat Component */}
      {showChat && customerId && (
        <Chat
          handymanName={customerName}
          handymanAvatar={null}
          isLive={isConnected}
          participantId={customerId}
          onCall={() => {
            if (customerPhone) {
              window.location.href = `tel:${customerPhone}`;
              toast.success(`Calling ${customerName.split(' ')[0]}...`);
            } else {
              toast.error("Customer phone number not available");
            }
          }}
          onEndChat={() => {
            setShowChat(false);
            toast.success("Chat ended");
          }}
        />
      )}

      {/* Dispute Dialog */}
      <DisputeDialog
        isOpen={showDisputeDialog}
        onClose={(disputeRaised) => {
          setShowDisputeDialog(false);
          if (disputeRaised) {
            setStatus("disputed");
            if (onBack) onBack();
          }
        }}
        taskId={taskId}
        userType="handyman"
      />
    </div>
  );
};

export default ActiveJobTracking;

